import { SkipToMainContent } from '@components/Misc';
import { SearchBarComponent } from '@components/SearchBar/SearchBarComponent';
import {
  transformMetadataCriteria,
  transformStatusCriteria,
} from '@pages/split/cameras/criteriaUtils';
import { buildCombinedCriteria, buildSearchCriteria } from '@pages/utils/helpers';
import {
  Button,
  Select, Table,
} from '@skyline-platform/alchemy';
import { getConfig } from '@utils/config';
import { exportToCSV } from '@utils/csvExport';
import { SSOT_ACTIONS } from '@utils/eventHandler';
import { getLogger } from '@utils/general';
import { getAppliances } from '@utils/graphql/creators/appliances';
import { getDevices } from '@utils/graphql/creators/devices';
import { getMetadata } from '@utils/graphql/creators/metadata';
import { getStreams } from '@utils/graphql/creators/streams';
import { getZones } from '@utils/graphql/creators/zones';
import { useMessageEmitter } from '@utils/react/eventEmitterContext';
import { useEffectOnce } from '@utils/react/hooks';
import { useServerPagination } from '@utils/react/hooks/useServerPagination';
import PropTypes from 'prop-types';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { configs } from './config';
import {
  formatExportData,
  mapCameraMetadataRow,
  parseInputUrl,
} from './helpers';
import './page.css';

const logger = getLogger('src.pages.reports.AllReports');

// Fields that map to integer criteria — only included when the search term is a valid integer.
// Including them with a non-numeric term causes a GraphQL type error.
const NUMBER_SEARCH_FIELDS = new Set(['streamInputPort']);

// Maps filterable column field names to backend GraphQL criteria keys, per report.
const FILTER_FIELD_MAP = {
  'Current Stream Status': {
    appliance: 'applianceName',
    ...(getConfig().JURISDICTION_FILTER_ENABLED === 'true' ? { jurisdiction: 'jurisdiction' } : {}),
    online: 'online',
    status: 'status', // Special handling for Public Zone Status - maps to zone_name + online
  },
  'Camera Metadata': {
    ...(getConfig().JURISDICTION_FILTER_ENABLED === 'true' ? { jurisdiction: 'jurisdiction' } : {}),
    ...(getConfig().CAMERA_CONNECTION_TYPE_ENABLED === 'true' ? { connection_type: 'connection_type' } : {}),
    // Temporarily disabled until backend supports sorting by these fields
    // routeName: 'routeName',           // ← changed from 'route_name'
    // trafficDirection: 'trafficDirection', // ← changed from 'traffic_direction'
    // mileMarker: 'mileMarker',         // ← changed from 'mile_marker'
  },
  'Stream Network Config': {
    appliance: 'applianceName',
  },
};

// --- Per-report server-side search field maps ---
const SEARCH_FIELD_MAP = {
  'Current Stream Status': {
    streamName: 'name',
    description: 'description',
    source: 'sourceName',
  },
  'Camera Metadata': {
    cameraName: 'name',
    description: 'description',
    network_address: 'network_address',
    latitude: 'latitudeText',
    longitude: 'longitudeText',
  },
  'Stream Network Config': {
    streamName: 'name',
    description: 'description',
    streamInputUrl: 'uri',
    streamInputMcast: 'mcastAddress',
    streamInputPort: 'mcastPort',
  },
};

const SORT_FIELD_MAP = {
  'Current Stream Status': {
    name: 'name',
    description: 'description',
    zone: 'zone_name',
    source: 'source_name',
    appliance: 'appliance_name',
    jurisdiction: 'jurisdiction',
  },
  'Camera Metadata': {
    name: 'name',
    description: 'description',
    network_address: 'network_address',
    connection_type: 'connection_type',
    latitude: 'latitude',
    longitude: 'longitude',
    jurisdiction: 'jurisdiction',
    routeName: 'route',
    trafficDirection: 'direction',
    mileMarker: 'mrm',
  },
  'Stream Network Config': {
    name: 'name',
    description: 'description',
    appliance: 'appliance_name',
    streamInputUrl: 'uri',
    streamInputMcast: 'mcast_address',
    streamInputPort: 'mcast_port',
  },
};

const FETCH_FIELDS = {
  'Current Stream Status': [
    'name',
    'description',
    'id',
    'appliance.id',
    'appliance.name',
    'source.id',
    'source.name',
    'zone.id',
    'zone.name',
    'online',
    'jurisdiction',
  ],
  'Camera Metadata': [
    'id',
    'name',
    'description',
    'network_address',
    'connection_type',
    'latitude',
    'longitude',
    'metadata',
  ],
  'Stream Network Config': [
    'name',
    'description',
    'id',
    'appliance.id',
    'appliance.name',
    'source.uri',
    'source.sdp_input',
    'source.port',
  ],
};

// Models whose SSOT events trigger a live refetch for each report type.
// Model names are lowercased before matching since SSOT casing is inconsistent.
const LIVE_UPDATE_MODELS = {
  'Current Stream Status': new Set([
    'stream',
    'zone',
    'appliance',
    'source',
  ]),
  'Camera Metadata': new Set(['device']),
  'Stream Network Config': new Set([
    'stream',
    'appliance',
    'source',
  ]),
};

// Wraps a zone name in a case-insensitive regex for GraphQL criteria.
const toZoneNameRegex = (zoneName) => `regex:(?i)^${String(zoneName).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`;

/**
 * Converts zone status column filters ({ status: ['Offline'], myZone_status: ['Online'] })
 * into the { zone_name, online } criterion pairs the backend expects, then merges them
 * with any regular search/filter criteria.
 *
 * Used by both applyCombinedCriteria (search path) and handleFilterChangeFromTable
 * (filter path) so the two code paths produce identical query shapes.
 */
function buildCurrentStreamCriteria(searchCriteria, columnFilters, filterMap) {
  const zoneStatusFilters = {};
  const regularFilters = {};
  Object.entries(columnFilters || {}).forEach(([field, values]) => {
    if (field === 'status' || field.endsWith('_status')) {
      zoneStatusFilters[field] = values;
    } else {
      regularFilters[field] = values;
    }
  });

  const zoneCriteria = [];
  Object.entries(zoneStatusFilters).forEach(([field, values]) => {
    const zoneName = field === 'status' ? 'Public' : field.slice(0, -'_status'.length);
    const zoneNameRegex = toZoneNameRegex(zoneName);
    values.forEach((statusValue) => {
      if (statusValue === 'Online') zoneCriteria.push({ zone_name: zoneNameRegex, online: true });
      else if (statusValue === 'Offline') zoneCriteria.push({ zone_name: zoneNameRegex, online: false });
    });
  });

  const searchAndRegularCriteria = buildCombinedCriteria(searchCriteria, regularFilters, filterMap);
  const hasBase = Object.keys(searchAndRegularCriteria).length > 0;

  if (zoneCriteria.length === 0) return searchAndRegularCriteria;
  if (zoneCriteria.length === 1) {
    return hasBase ? { ...searchAndRegularCriteria, ...zoneCriteria[0] } : zoneCriteria[0];
  }
  return hasBase
    ? { or: zoneCriteria.map((zf) => ({ ...searchAndRegularCriteria, ...zf })) }
    : { or: zoneCriteria };
}

// Server always handles filtering — bypass the Table's client-side filter logic.
const SERVER_FILTER_MATCHER = () => true;

const ZONE_STATUS_FILTER_OPTIONS = [{ label: 'Online', value: 'Online' }, { label: 'Offline', value: 'Offline' }];


export const AllReports = ({ setTableTitle }) => {
  const reportOptions = [
    'Current Stream Status',
    'Camera Metadata',
    'Stream Network Config',
  ];

  const [selectedReport, setSelectedReport] = useState('Current Stream Status');
  const [selectedFields, setSelectedFields] = useState(
    configs['Current Stream Status'].searchOptions.map((o) => o.value)
  );
  const [exporting, setExporting] = useState(false);
  // Jurisdiction options for Camera Metadata report (fetched once on mount) — used in the filter dropdown and to build dynamic search criteria.
  const [jurisdictionOptions, setJurisdictionOptions] = useState([]);
  // Route Name options for Camera Metadata report (fetched once on mount) — used in the filter dropdown
  const [routeNameFilterOptions, setRouteNameFilterOptions] = useState([]);
  // Traffic Direction options for Camera Metadata report (fetched once on mount) — used in the filter dropdown
  const [trafficDirectionFilterOptions, setTrafficDirectionFilterOptions] = useState([]);
  // Mile Marker options for Camera Metadata report (fetched once on mount) — used in the filter dropdown
  const [mileMarkerFilterOptions, setMileMarkerFilterOptions] = useState([]);
  // Zones used to build dynamic columns for Current Stream Status
  const [zones, setZones] = useState([]);
  // The zone name currently being prioritized in the sort (Current Stream Status only).
  // When set, streams in that zone appear first (sorted by online status).
  const [zonePriority, setZonePriority] = useState(null);
  // Ref so handleServerSearch can compare current zone without it being a dep
  const zonePriorityRef = useRef(null);
  const jurisdictionFilterEnabled = getConfig().JURISDICTION_FILTER_ENABLED === 'true';

  useEffect(() => { zonePriorityRef.current = zonePriority; }, [zonePriority]);
  useEffect(() => { setTableTitle(selectedReport); }, [selectedReport, setTableTitle]);

  // Extracted so the live update handler can also call it (e.g. on zone rename).
  const fetchZones = useCallback((onFetched) => {
    const { query, execute } = getZones();
    query.addReturnProperties('id', 'name');
    execute()
      .then((res) => {
        const allZones = Array.isArray(res?.data) ? res.data : [];
        const nonPublic = [
          ...new Set(
            allZones.map((z) => z.name).filter((n) => n && n.toLowerCase() !== 'public')
          ),
        ];
        setZones(nonPublic);
        setSelectedFields((prev) => {
          const zoneValues = ['zone__Public', ...nonPublic.map((z) => `zone__${z}`)];
          const withoutStale = prev.filter((f) => !f.startsWith('zone__'));
          return [...withoutStale, ...zoneValues];
        });
        onFetched?.();
      })
      .catch((err) => logger.error('Failed to fetch zones for report', err));
  }, []);

  // Fetch all zone names when Current Stream Status is selected
  useEffect(() => {
    if (selectedReport !== 'Current Stream Status') {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern, sync update is intentional
      setZones([]);
      return;
    }
    fetchZones();
  }, [selectedReport, fetchZones]);

  // --- mapFn per report type ---
  const zonesRef = useRef(zones);
  useEffect(() => { zonesRef.current = zones; }, [zones]);

  // mapStreamStatus reads zonesRef.current instead of closing over zones directly.
  // This keeps the callback reference stable across zone loads, so fetchPage is not
  // recreated (and a spurious extra fetch is not triggered) when zones arrive.
  // A dedicated refetch is triggered below once zones are ready.
  const mapStreamStatus = useCallback((streams) => {
    const currentZones = zonesRef.current;
    return streams.map((stream) => {
      const streamZoneName = stream.zone?.name ?? '';
      const row = {
        id: stream.id,
        name: stream.name,
        description: stream.description ?? '',
        source: stream.source?.name ?? '',
        appliance: stream.appliance?.name ?? '',
        jurisdiction: stream?.jurisdiction ?? '',
        online: stream.online,
        zoneName: streamZoneName,
        zoneId: stream.zone?.id ?? null,
        applianceId: stream.appliance?.id ?? null,
        sourceId: stream.source?.id ?? null,
        status: streamZoneName.toLowerCase() === 'public'
          ? (stream.online ? 'Online' : 'Offline')
          : '',
      };
      currentZones.forEach((zoneName) => {
        row[`${zoneName}_status`] = streamZoneName.toLowerCase() === zoneName.toLowerCase()
          ? (stream.online ? 'Online' : 'Offline')
          : '';
      });
      return row;
    });
  }, []);

  const mapCameraMetadata = useCallback((devices) => devices.map(mapCameraMetadataRow), []);

  const mapStreamNetworkConfig = useCallback((streams) => {
    return streams.map((stream) => {
      const inputUrl = stream.source?.uri ?? '';
      const { addr, port } = parseInputUrl(inputUrl);
      return {
        id: stream.id,
        name: stream.name,
        description: stream.description ?? '',
        appliance: stream.appliance?.name ?? '',
        applianceId: stream.appliance?.id ?? null,
        streamInputUrl: inputUrl,
        streamInputMcast: addr ?? '',
        streamInputPort: port ?? '',
      };
    });
  }, []);

  const extraOptions = useMemo(() => (
    zonePriority ? { zone_priority: zonePriority } : {}
  ), [zonePriority]);

  const fetchFn = useMemo(() => {
    if (selectedReport === 'Camera Metadata') return getDevices;
    return getStreams;
  }, [selectedReport]);

  const mapFn = useMemo(() => {
    if (selectedReport === 'Current Stream Status') return mapStreamStatus;
    if (selectedReport === 'Camera Metadata') return mapCameraMetadata;
    return mapStreamNetworkConfig;
  }, [
    selectedReport,
    mapStreamStatus,
    mapCameraMetadata,
    mapStreamNetworkConfig,
  ]);

  const {
    rows,
    totalRows,
    loading,
    currentPage,
    rowsPerPage,
    handleSort,
    setSortState,
    sortDirection,
    handlePageChange,
    handleRowsPerPageChange,
    handleSearch,
    clearSearch,
    reset,
    refetchCurrentPage,
    fetchAll,
    setRows,
  } = useServerPagination({
    fetchFn,
    mapFn,
    fetchFields: FETCH_FIELDS[selectedReport],
    defaultSort: { field: 'name', direction: 'asc' },
    sortFieldMap: SORT_FIELD_MAP[selectedReport],
    enableSort: true,
    extraOptions: selectedReport === 'Camera Metadata'
      ? { ...extraOptions, includePrivateMetadata: true }
      : extraOptions,
    onError: (error) => logger.error(error),
  });

  // Stable refs for the live update handler — avoids recreating the subscription on each render.
  const selectedReportRef = useRef(selectedReport);
  useEffect(() => { selectedReportRef.current = selectedReport; }, [selectedReport]);


  // Reset pagination/search when switching reports, but skip the initial mount —
  // the hook already starts with the correct initial state. Calling reset() on mount
  // changes the criteria reference, causing a spurious second fetch.
  const isFirstRender = useRef(true);
  const hasActiveCriteriaRef = useRef(false);
  const activeFiltersRef = useRef({});
  const lastSearchCriteriaRef = useRef({});
  const lastAppliedFilterEventRef = useRef('');
  const lastAppliedCriteriaRef = useRef('');

  // Appliance filter options for Stream Network Config (deduped from all appliances, fetched once per mount)
  const [applianceFilterOptions, setApplianceFilterOptions] = useState([]);

  // Fetch all appliances (id, name, hostname) on mount (once)
  useEffect(() => {
    let cancelled = false;
    async function fetchAllAppliances() {
      const { query, execute } = getAppliances();
      query.addReturnProperties('id', 'name');
      const result = await execute();
      if (cancelled || !Array.isArray(result?.data)) return;
      // Dedupe by id
      const map = new Map();
      for (const appliance of result.data) {
        if (appliance && appliance.id && !map.has(appliance.id)) {
          map.set(appliance.id, { label: appliance.name || '', value: appliance.name || '' });
        }
      }
      setApplianceFilterOptions(Array.from(map.values()).filter((opt) => opt.value));
    }
    fetchAllAppliances();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!jurisdictionFilterEnabled) return;
    let cancelled = false;
    async function fetchJurisdictions() {
      try {
        const { execute } = await getMetadata({ key: 'regex:jurisdiction' });
        await execute().then(({ error, data }) => {
          if (error) logger.error(error);
          if (!cancelled && data && data.length > 0) {
            const uniqueOptions = Array.from(new Set(data.map((j) => j.value).filter(Boolean)))
              .sort().map((value) => ({ label: value, value }));
            setJurisdictionOptions(uniqueOptions);
          }
        });
      } catch (err) {
        logger.error('Failed to fetch jurisdictions', err);
      }
    }
    fetchJurisdictions();
    return () => { cancelled = true; };
  }, [jurisdictionFilterEnabled]);

  useEffect(() => {
    let cancelled = false;
    async function fetchRouteNames() {
      try {
        const { execute } = await getMetadata({ key: 'regex:route name' });
        await execute().then(({ error, data }) => {
          if (error) logger.error(error);
          if (!cancelled && data && data.length > 0) {
            const uniqueOptions = Array.from(new Set(data.map((r) => r.value).filter(Boolean)))
              .sort().map((value) => ({ label: value, value }));
            setRouteNameFilterOptions(uniqueOptions);
          }
        });
      } catch (err) {
        logger.error('Failed to fetch route names', err);
      }
    }
    fetchRouteNames();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function fetchTrafficDirections() {
      try {
        const { execute } = await getMetadata({ key: 'regex:traffic direction' });
        await execute().then(({ error, data }) => {
          if (error) logger.error(error);
          if (!cancelled && data && data.length > 0) {
            const uniqueOptions = Array.from(new Set(data.map((r) => r.value).filter(Boolean)))
              .sort().map((value) => ({ label: value, value }));
            setTrafficDirectionFilterOptions(uniqueOptions);
          }
        });
      } catch (err) {
        logger.error('Failed to fetch traffic directions', err);
      }
    }
    fetchTrafficDirections();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function fetchMileMarkers() {
      try {
        const { execute } = await getMetadata({ key: 'regex:mile marker' });
        await execute().then(({ error, data }) => {
          if (error) logger.error(error);
          if (!cancelled && data && data.length > 0) {
            const uniqueOptions = Array.from(new Set(data.map((r) => r.value).filter(Boolean)))
              .sort().map((value) => ({ label: value, value }));
            setMileMarkerFilterOptions(uniqueOptions);
          }
        });
      } catch (err) {
        logger.error('Failed to fetch mile markers', err);
      }
    }
    fetchMileMarkers();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    reset();
    setZonePriority(null);
    setSelectedFields(configs[selectedReport]?.searchOptions?.map((o) => o.value) ?? []);
    activeFiltersRef.current = {};
    lastSearchCriteriaRef.current = {};
    lastAppliedFilterEventRef.current = '';
    lastAppliedCriteriaRef.current = '';
  }, [selectedReport, reset]);

  const zonesLoadedRef = useRef(false);
  const initialZonesLoadedRef = useRef(false);
  useEffect(() => {
    if (zones.length === 0) {
      zonesLoadedRef.current = false;
      return;
    }
    if (!zonesLoadedRef.current) {
      zonesLoadedRef.current = true;
      if (!initialZonesLoadedRef.current) {
        initialZonesLoadedRef.current = true;
        if (!hasActiveCriteriaRef.current) {
          refetchCurrentPage();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zones]);

  const handleSortWithZoneClear = useCallback((column, direction) => {
    if (column.field?.endsWith('_status') || column.field === 'status') {
      const zoneName = column.field === 'status' ? 'Public' : column.field.slice(0, -'_status'.length);
      setZonePriority(zoneName);
      setSortState('online', direction);
    } else {
      setZonePriority(null);
      handleSort(column, direction);
    }
  }, [handleSort, setSortState]);

  const applyCombinedCriteria = useCallback((searchCriteria, columnFilters) => {
    const filterMap = FILTER_FIELD_MAP[selectedReport] ?? {};

    let combined;
    if (selectedReport === 'Current Stream Status') {
      combined = buildCurrentStreamCriteria(searchCriteria, columnFilters, filterMap);
    } else {
      combined = buildCombinedCriteria(searchCriteria, columnFilters, filterMap);
      if (selectedReport === 'Camera Metadata') {
        combined = transformMetadataCriteria(transformStatusCriteria(combined));
      }
    }

    const hasCriteria = Object.keys(combined).length > 0;
    const combinedJson = JSON.stringify(combined);
    if (hasCriteria) {
      if (combinedJson === lastAppliedCriteriaRef.current) return;
      lastAppliedCriteriaRef.current = combinedJson;
      hasActiveCriteriaRef.current = true;
      handleSearch(combined);
    } else if (hasActiveCriteriaRef.current) {
      lastAppliedCriteriaRef.current = '';
      hasActiveCriteriaRef.current = false;
      clearSearch();
    }
  }, [
    selectedReport,
    handleSearch,
    clearSearch,
  ]);

  const handleFilterChangeFromTable = useCallback((columnFilters) => {
    const filterJson = JSON.stringify(columnFilters);
    if (filterJson === lastAppliedFilterEventRef.current) return;
    lastAppliedFilterEventRef.current = filterJson;

    const isEmptyFilters = Object.keys(columnFilters).length === 0;
    // Only block empty filter calls if there were no active filters before —
    // that means it's a spurious call from a Table remount, not a user-initiated clear.
    const hadActiveFilters = Object.keys(activeFiltersRef.current).length > 0;
    if (isEmptyFilters && hasActiveCriteriaRef.current && !hadActiveFilters) return;

    activeFiltersRef.current = columnFilters;

    if (selectedReport === 'Current Stream Status') {
      const filterMap = FILTER_FIELD_MAP[selectedReport] ?? {};
      // Detect whether any zone-status filters are present so we can clear zonePriority
      // when they're absent or when multiple zones are active.
      const hasZoneStatusFilters = Object.keys(columnFilters).some(
        (f) => f === 'status' || f.endsWith('_status')
      );
      const multipleZones = Object.entries(columnFilters).reduce((count, [field, values]) => {
        if ((field === 'status' || field.endsWith('_status')) && values.length > 0) return count + values.length;
        return count;
      }, 0) > 1;
      if (!hasZoneStatusFilters || multipleZones) setZonePriority(null);

      const finalCriteria = buildCurrentStreamCriteria(lastSearchCriteriaRef.current, columnFilters, filterMap);

      const hasCriteria = Object.keys(finalCriteria).length > 0;
      if (hasCriteria) {
        const finalCriteriaJson = JSON.stringify(finalCriteria);
        if (finalCriteriaJson !== lastAppliedCriteriaRef.current) {
          lastAppliedCriteriaRef.current = finalCriteriaJson;
          hasActiveCriteriaRef.current = true;
          handleSearch(finalCriteria);
        }
      } else if (hasActiveCriteriaRef.current) {
        lastAppliedCriteriaRef.current = '';
        hasActiveCriteriaRef.current = false;
        clearSearch();
      }
    } else {
      applyCombinedCriteria(lastSearchCriteriaRef.current, columnFilters);
    }
  }, [
    selectedReport,
    applyCombinedCriteria,
    handleSearch,
    clearSearch,
  ]);

  const handleServerSearch = useCallback((searchTerm, fields) => {
    const zoneFields = fields.filter((f) => f.startsWith('zone__'));
    const regularFields = fields.filter((f) => !f.startsWith('zone__'));
    if (!searchTerm) {
      lastSearchCriteriaRef.current = {};
      applyCombinedCriteria({}, activeFiltersRef.current);
      return;
    }
    if (zonePriorityRef.current !== null) {
      setZonePriority(null);
      setSortState('name', 'asc');
    }
    const isInteger = Number.isInteger(Number(searchTerm.trim()));
    const nonNumberFields = regularFields.filter((f) => !NUMBER_SEARCH_FIELDS.has(f));
    const fieldMap = SEARCH_FIELD_MAP[selectedReport] ?? {};
    const regularCriteria = buildSearchCriteria(searchTerm, nonNumberFields, fieldMap);
    if (isInteger) {
      const intValue = Number(searchTerm.trim());
      regularFields.filter((f) => NUMBER_SEARCH_FIELDS.has(f)).forEach((f) => {
        const backendField = fieldMap[f];
        if (backendField) {
          if (regularCriteria.or) {
            regularCriteria.or.push({ [backendField]: intValue });
          } else if (Object.keys(regularCriteria).length > 0) {
            regularCriteria.or = [regularCriteria, { [backendField]: intValue }];
            Object.keys(regularCriteria).forEach((k) => { if (k !== 'or') delete regularCriteria[k]; });
          } else {
            regularCriteria[backendField] = intValue;
          }
        }
      });
    }
    if (zoneFields.length > 0) {
      const term = searchTerm.trim().toLowerCase();
      const onlineValue = term === 'online' ? true : term === 'offline' ? false : null;
      if (onlineValue !== null) {
        const zoneOrItems = zoneFields.map((f) => ({
          zone_name: toZoneNameRegex(f.slice('zone__'.length)),
          online: onlineValue,
        }));
        let regularOrItems = [];
        if (Object.keys(regularCriteria).length > 0) {
          regularOrItems = regularCriteria.or ?? [regularCriteria];
        }
        const allOrItems = [...regularOrItems, ...zoneOrItems];
        const criteria = allOrItems.length === 1 ? allOrItems[0] : { or: allOrItems };
        if (Object.keys(criteria).length === 0) return;
        hasActiveCriteriaRef.current = true;
        handleSearch(criteria);
        return;
      }
    }
    if (Object.keys(regularCriteria).length === 0) return;
    lastSearchCriteriaRef.current = regularCriteria;
    applyCombinedCriteria(regularCriteria, activeFiltersRef.current);
  }, [
    selectedReport,
    applyCombinedCriteria,
    handleSearch,
    setZonePriority,
    setSortState,
  ]);

  // Live updates: patch rows inline via setRows — no server round-trip, no loading indicator.
  //
  // Stream/device scalar fields are patched directly. When a stream's linked assignment
  // changes (zone_id, appliance_id, source_id), a targeted single-stream fetch resolves
  // the new display names and patches only that row. Linked entity renames (zone/appliance/
  // source model events) are handled inline by matching on previous.name. Create events
  // are skipped — server pagination means a new record could land on any page.
  const handleLiveUpdate = useCallback((eventMessage) => {
    const payload = eventMessage?.detail?.payload;
    if (!payload) return;
    const model = payload.model?.toLowerCase();
    const report = selectedReportRef.current;
    if (!model || !LIVE_UPDATE_MODELS[report]?.has(model)) return;
    const rawAction = payload.action;
    const action = rawAction
      ? rawAction.charAt(0).toUpperCase() + rawAction.slice(1).toLowerCase()
      : null;
    if (!action) return;
    let data;
    try {
      data = typeof payload.data === 'string' ? JSON.parse(payload.data) : payload.data;
    } catch { return; }
    if (!data) return;
    const current = data.current ?? {};
    const previous = data.previous ?? {};
    if (action === SSOT_ACTIONS.REMOVE) {
      const removedId = previous?.id ?? current?.id;
      if (removedId && (model === 'stream' || model === 'device')) {
        setRows((prev) => prev.filter((row) => row.id !== removedId));
      }
      return;
    }
    if (action !== SSOT_ACTIONS.UPDATE) return;
    // ── Current Stream Status ────────────────────────────────────────────────
    if (report === 'Current Stream Status') {
      if (model === 'stream' && current?.id) {
        // If a linked assignment changed (zone/appliance/source reassigned), the display
        // name is not in the SSOT payload — fetch the full stream and re-map that row.
        const zoneChanged = 'zone_id' in current && current.zone_id !== previous?.zone_id;
        const applianceChanged = 'appliance_id' in current && current.appliance_id !== previous?.appliance_id;
        const sourceChanged = 'source_id' in current && current.source_id !== previous?.source_id;
        if (zoneChanged || applianceChanged || sourceChanged) {
          const { query, execute } = getStreams(current.id);
          query.addReturnProperties(...FETCH_FIELDS['Current Stream Status']);
          execute()
            .then((res) => {
              const raw = res?.data;
              const fetched = Array.isArray(raw) ? raw[0] : raw;
              if (!fetched) return;
              const [mapped] = mapStreamStatus([fetched]);
              setRows((prev) => prev.map((row) => row.id === fetched.id ? mapped : row));
            })
            .catch((err) => logger.error(err));
          return;
        }
        // Inline patch for scalar-only changes (name, description, online status)
        setRows((prev) => prev.map((row) => {
          if (row.id !== current.id) return row;
          const patch = {};
          if ('name' in current) patch.name = current.name ?? '';
          if ('description' in current) patch.description = current.description ?? '';
          if ('online' in current) {
            patch.online = current.online;
            patch.status = row.zoneName?.toLowerCase() === 'public'
              ? (current.online ? 'Online' : 'Offline')
              : '';
            zonesRef.current.forEach((zoneName) => {
              patch[`${zoneName}_status`] = row.zoneName?.toLowerCase() === zoneName.toLowerCase()
                ? (current.online ? 'Online' : 'Offline')
                : '';
            });
          }
          return { ...row, ...patch };
        }));
        return;
      }
      if (model === 'zone') {
        // Zone renamed: rebuild zone column headers via fetchZones.
        // Also inline-patch zoneName / status fields for affected rows so they stay consistent.
        // Match by zoneId (more robust than previous.name — doesn't depend on previous state).
        if (current.name && current.id) {
          setRows((prev) => prev.map((row) => {
            if (row.zoneId !== current.id) return row;
            // Use the row's current zoneName as the old column key before renaming
            const oldKey = `${row.zoneName}_status`;
            const newKey = `${current.name}_status`;
            return {
              ...row, zoneName: current.name, [oldKey]: '', [newKey]: row[oldKey] ?? '',
            };
          }));
          fetchZones();
        }
        return;
      }
      if (model === 'appliance' && 'name' in current && current.id) {
        // Appliance renamed: update the appliance column for all affected rows
        setRows((prev) => prev.map((row) =>
          row.applianceId === current.id ? { ...row, appliance: current.name ?? '' } : row
        ));
        return;
      }
      if (model === 'source' && 'name' in current && current.id) {
        // Source (stream profile) renamed: update the source column for all affected rows
        setRows((prev) => prev.map((row) =>
          row.sourceId === current.id ? { ...row, source: current.name ?? '' } : row
        ));
        return;
      }
    }
    // ── Camera Metadata ──────────────────────────────────────────────────────
    if (report === 'Camera Metadata' && model === 'device' && current?.id) {
      setRows((prev) => prev.map((row) => {
        if (row.id !== current.id) return row;
        const patch = {};
        if ('name' in current) patch.name = current.name ?? '';
        if ('description' in current) patch.description = current.description ?? '';
        if ('network_address' in current) patch.network_address = current.network_address ?? '';
        if ('connection_type' in current) patch.connection_type = current.connection_type ?? '';
        if ('latitude' in current) patch.latitude = current.latitude ?? '';
        if ('longitude' in current) patch.longitude = current.longitude ?? '';
        return { ...row, ...patch };
      }));
      return;
    }
    // ── Stream Network Config ────────────────────────────────────────────────
    if (report === 'Stream Network Config') {
      if (model === 'stream' && current?.id) {
        // If appliance assignment changed, fetch the full stream to resolve the new name.
        const applianceChanged = 'appliance_id' in current && current.appliance_id !== previous?.appliance_id;
        if (applianceChanged) {
          const { query, execute } = getStreams(current.id);
          query.addReturnProperties(...FETCH_FIELDS['Stream Network Config']);
          execute()
            .then((res) => {
              const raw = res?.data;
              const fetched = Array.isArray(raw) ? raw[0] : raw;
              if (!fetched) return;
              const [mapped] = mapStreamNetworkConfig([fetched]);
              setRows((prev) => prev.map((row) => row.id === fetched.id ? mapped : row));
            })
            .catch((err) => logger.error(err));
          return;
        }
        setRows((prev) => prev.map((row) => {
          if (row.id !== current.id) return row;
          const patch = {};
          if ('name' in current) patch.name = current.name ?? '';
          if ('description' in current) patch.description = current.description ?? '';
          return { ...row, ...patch };
        }));
        return;
      }
      if (model === 'appliance' && 'name' in current && current.id) {
        setRows((prev) => prev.map((row) =>
          row.applianceId === current.id ? { ...row, appliance: current.name ?? '' } : row
        ));
      }
    }
  }, [
    setRows,
    fetchZones,
    mapStreamStatus,
    mapStreamNetworkConfig,
  ]);

  const messageHandler = useMessageEmitter();
  useEffectOnce(() => {
    messageHandler.subscribe(handleLiveUpdate);
    return () => { messageHandler.unsubscribe(handleLiveUpdate); };
  });

  // --- Dynamic column/searchOption computation ---
  const searchOptions = useMemo(() => {
    return configs[selectedReport]?.searchOptions ?? [];
  }, [selectedReport]);

  const connectionTypeEnabled = getConfig().CAMERA_CONNECTION_TYPE_ENABLED === 'true';

  const tableColumns = useMemo(() => {
    const base = configs[selectedReport]?.tableColumns ?? [];
    if (selectedReport === 'Current Stream Status') {
      // Patch appliance and status columns for filterOptions
      const patched = base.map((col) => {
        if (col.field === 'appliance') {
          return {
            ...col,
            filterable: true,
            filterOptions: applianceFilterOptions,
            filterMatcher: SERVER_FILTER_MATCHER,
          };
        }
        if (col.field === 'status') {
          return {
            ...col,
            filterable: true,
            filterOptions: ZONE_STATUS_FILTER_OPTIONS,
            filterMatcher: SERVER_FILTER_MATCHER,
          };
        }
        if (col.field === 'jurisdiction') {
          return {
            ...col,
            filterable: jurisdictionFilterEnabled,
            filterOptions: jurisdictionFilterEnabled ? jurisdictionOptions : [],
          };
        }
        return col;
      });
      // Add dynamic zone columns
      if (zones.length > 0) {
        return [
          ...patched,
          ...zones.map((zoneName) => ({
            field: `${zoneName}_status`,
            headerName: `${zoneName} Status`,
            type: 'string',
            grow: 1,
            sortable: true,
            filterable: true,
            filterOptions: ZONE_STATUS_FILTER_OPTIONS,
            filterMatcher: SERVER_FILTER_MATCHER,
          })),
        ];
      }
      return patched;
    }
    if (selectedReport === 'Camera Metadata') {
      return base
        .filter((col) => col.field !== 'connection_type' || connectionTypeEnabled)
        .map((col) => {
          if (col.field === 'connection_type') {
            return {
              ...col,
              filterable: true,
              filterOptions: [
                { label: 'Radio', value: 'Radio' },
                { label: 'Cellular', value: 'Cellular' },
                { label: 'Fiber', value: 'Fiber' },
              ],
              filterMatcher: SERVER_FILTER_MATCHER,
            };
          }
          if (col.field === 'jurisdiction') {
            return {
              ...col,
              filterable: jurisdictionFilterEnabled,
              filterOptions: jurisdictionFilterEnabled ? jurisdictionOptions : [],
              filterMatcher: SERVER_FILTER_MATCHER,
            };
          }
          if (col.field === 'routeName') {
            return {
              ...col,
              // Temporarily disabling filterable for routeName until we can confirm the sorting behavior
              // filterable: true,
              filterOptions: routeNameFilterOptions,
              filterMatcher: SERVER_FILTER_MATCHER,
            };
          }
          if (col.field === 'trafficDirection') {
            return {
              ...col,
              // Temporarily disabling filterable for routeName until we can confirm the sorting behavior
              // filterable: true,
              filterOptions: trafficDirectionFilterOptions,
              filterMatcher: SERVER_FILTER_MATCHER,
            };
          }
          if (col.field === 'mileMarker') {
            return {
              ...col,
              // Temporarily disabling filterable for routeName until we can confirm the sorting behavior
              // filterable: true,
              filterOptions: mileMarkerFilterOptions,
              filterMatcher: SERVER_FILTER_MATCHER,
            };
          }
          return col;
        });
    }
    if (selectedReport === 'Stream Network Config') {
      return base.map((col) =>
        col.field === 'appliance'
          ? {
            ...col,
            filterable: true,
            filterOptions: applianceFilterOptions,
            filterMatcher: SERVER_FILTER_MATCHER,
          }
          : col
      );
    }
    return base;
  }, [
    selectedReport,
    zones,
    applianceFilterOptions,
    jurisdictionFilterEnabled,
    jurisdictionOptions,
    connectionTypeEnabled,
    routeNameFilterOptions,
    trafficDirectionFilterOptions,
    mileMarkerFilterOptions,
  ]);

  return (
    <gar-all-reports>
      <div className='actions-bar' style={{
        display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center',
      }}>
        <Select
          id='report-select'
          lineSeparator
          options={reportOptions}
          onSelectChange={setSelectedReport}
          value={selectedReport}
        />
        {searchOptions.length > 0 && (
          <SearchBarComponent
            key={selectedReport}
            searchOptions={searchOptions}
            selectedFields={selectedFields}
            setSelectedFields={setSelectedFields}
            fieldName='Reports'
            onSearch={handleServerSearch}
          />
        )}
        <Button
          label={exporting ? 'Exporting...' : 'Export to CSV'}
          title={totalRows === 0 ? 'There is no data to export' : 'Export to CSV'}
          iconLeft='fa-solid fa-download'
          state={totalRows === 0 || exporting ? 'disabled' : 'default'}
          type='filled'
          onClick={async () => {
            setExporting(true);
            try {
              const allRecords = await fetchAll(FETCH_FIELDS[selectedReport]);
              const allMapped = mapFn(allRecords);
              const exportData = formatExportData(allMapped, tableColumns);
              exportToCSV(exportData, `${selectedReport}.csv`);
            } finally {
              setExporting(false);
            }
          }}
        />
      </div>
      <SkipToMainContent
        text="Skip to pagination controls"
        targetSelector="alc-pagination-bar"
      />
      <div>
        <Table
          tableName='Reports'
          key={selectedReport}
          tabIndex={-1}
          columns={tableColumns}
          getRowId={(row) => row.id}
          rows={rows}
          initialSortField='name'
          defaultSortAsc={true}
          sortServer
          onSort={handleSortWithZoneClear}
          onFilterChange={handleFilterChangeFromTable}
          paginationServer
          paginationTotalRows={totalRows}
          currentPage={currentPage}
          paginationPerPage={rowsPerPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          progressPending={loading}
          sortDirection={sortDirection}
          style={{ height: rows.length === 0 && !loading ? '400px' : undefined }}
        />
      </div>
    </gar-all-reports>
  );
};

AllReports.propTypes = {
  setTableTitle: PropTypes.func.isRequired,
};